export const HYBRID_QR_STATE = {
  CONNECTED: 'connected',
  WAITING: 'waiting',
  DISCONNECTED: 'disconnected',
  NOT_CONFIGURED: 'not_configured',
};

export const HYBRID_QR_SOURCE = {
  MANAGED: 'managed',
  NATIVE: 'native',
  LINKED: 'linked',
  MANUAL: 'manual',
};

const stateOf = connection => {
  if (!connection?.connection || connection.connection === 'not_configured')
    return HYBRID_QR_STATE.NOT_CONFIGURED;
  if (connection.connection === 'connecting') return HYBRID_QR_STATE.WAITING;
  if (connection.connection === 'open' && connection.identity_verified)
    return HYBRID_QR_STATE.CONNECTED;
  return HYBRID_QR_STATE.DISCONNECTED;
};

const attendanceSource = attendance => {
  if (attendance.native) return HYBRID_QR_SOURCE.NATIVE;
  return attendance.managed
    ? HYBRID_QR_SOURCE.MANAGED
    : HYBRID_QR_SOURCE.MANUAL;
};

const last4 = value =>
  String(value || '')
    .replace(/\D/g, '')
    .slice(-4);

/**
 * The QR side of a hybrid official inbox, read the same way wherever it is shown (the settings card
 * and the disconnected banner), so the two never disagree.
 *
 * A linked QR inbox owns its session: its state comes from that inbox in the store, which the cable keeps
 * current, not from the snapshot embedded in the official inbox.
 *
 * @param {Object} inbox - the official inbox
 * @param {Array} [inboxes] - every inbox in the store
 * @returns {{source: string|null, state: string, phoneLast4: string, checkedAt: string|null, error: string|null,
 *   qrInboxId: number|null, qrInboxName: string|null}}
 */
export const hybridQrConnection = (inbox, inboxes = []) => {
  const linked = inbox?.linked_qr_connection;
  if (linked) {
    const qrInbox = inboxes.find(item => item.id === linked.qr_inbox_id);
    const live = qrInbox?.provider_connection;
    const connection = live
      ? {
          connection: live.connection,
          identity_verified: live.connection === 'open',
          error: live.error,
        }
      : linked;
    return {
      source: HYBRID_QR_SOURCE.LINKED,
      state: stateOf(connection),
      phoneLast4: last4(live?.phone_number) || linked.phone_last4 || '',
      checkedAt: null,
      error: connection.error || null,
      qrInboxId: linked.qr_inbox_id || null,
      qrInboxName: qrInbox?.name || linked.qr_inbox_name || null,
    };
  }

  const attendance = inbox?.attendance_connection;
  if (!attendance) {
    return {
      source: null,
      state: HYBRID_QR_STATE.NOT_CONFIGURED,
      phoneLast4: '',
      checkedAt: null,
      error: null,
      qrInboxId: null,
      qrInboxName: null,
    };
  }
  return {
    source: attendanceSource(attendance),
    state: stateOf(attendance),
    phoneLast4: attendance.phone_last4 || '',
    checkedAt: attendance.checked_at || null,
    error: attendance.error || null,
    qrInboxId: null,
    qrInboxName: null,
    providerName: attendance.provider_name || null,
  };
};

/**
 * Whether the QR connection of a hybrid inbox dropped while the account relies on it. A connection that was
 * never set up, or is waiting for its QR to be read, is not a drop.
 */
export const isHybridQrDown = (inbox, inboxes = []) =>
  inbox?.hybrid_attendance_enabled === true &&
  hybridQrConnection(inbox, inboxes).state === HYBRID_QR_STATE.DISCONNECTED;
